/**
 * Product color scales (design tokens). Use via `colors` and `getInsideChrome(theme)` for authenticated shell UI.
 * Primary / success / error / warning / neutral weights match the shared Color System doc.
 */

/** @type {const} */
export const colors = {
    background:{
        50: '#ffffff'
    },
    primary: {
        50: '#faf5ff',
        100: '#f3e8ff',
        200: '#e9d5ff',
        300: '#d8b4fe',
        400: '#c084fc',
        500: '#a855f7',
        600: '#9333ea',
        700: '#7e22ce',
        800: '#6b21a8',
        900: '#581c87',
    },
    success: {
        50: '#f0fdf4',
        100: '#dcfce7',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
    },
    error: {
        50: '#fef2f2',
        100: '#fee2e2',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c',
    },
    warning: {
        50: '#fffbeb',
        100: '#fef3c7',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309',
    },
    neutral: {
        0: '#ffffff',
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#e5e5e5',
        500: '#737373',
        900: '#171717',
    },
};

const DARK_BG = '#141414';

function clamp01(a) {
    return Math.min(1, Math.max(0, Number(a)));
}

/**
 * @param {string} hex — `#RGB`, `#RRGGBB`, or `#RRGGBBAA` (alpha ignored)
 * @param {number} alpha — 0…1
 * @returns {string} `rgba(r,g,b,a)`
 */
export function rgbaFromHex(hex, alpha) {
    if (!hex || typeof hex !== 'string') return `rgba(0,0,0,${clamp01(alpha)})`;
    let h = hex.trim().replace('#', '');
    if (h.length === 3) {
        h = h
            .split('')
            .map((c) => c + c)
            .join('');
    }
    if (h.length < 6) return `rgba(0,0,0,${clamp01(alpha)})`;
    const bigint = parseInt(h.slice(0, 6), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${clamp01(alpha)})`;
}

/** True when app dark shell is active (matches theme `background.default`). */
export function insideIsDark(theme) {
    return theme?.colors?.background?.default === DARK_BG;
}

/** Selected pill behind rail / feature nav — primary 600 @ ~10% opacity */
export const moduleItemSelectedBg = rgbaFromHex(colors.primary[600], 0.1);

/** Module rail + black module column: selected row (#7543DC @ ~10% fill, brand icon) */
export const moduleSidebarSelectedAccent = '#7543DC';
export const moduleSidebarSelectedFill = rgbaFromHex(moduleSidebarSelectedAccent, 0.1);

/** Legacy layout accent (header icon selected wash) — primary 700 @ ~12% */
export const layoutAccentWash = rgbaFromHex(colors.primary[700], 0.12);

/**
 * Icon / divider / border on the fixed white module-rail card (same in app light and dark).
 */
export function getModuleRailWhiteSurfaceChrome() {
    const c = colors;
    return {
        railIdleIcon: '#A3A3A3',
        railHoverFg: c.neutral[900],
        railHoverBg: c.neutral[100],
        moduleRailBorder: '#EEEEEE',
        moduleRailDivider: c.neutral[200],
        onlineDotRing: c.neutral[0],
    };
}

/**
 * Semantic colors for authenticated inside chrome (module rail, feature sidebar, header token bar).
 * All values come from `colors` + `insideIsDark(theme)`; pass styled-components `theme`.
 * @param {import('styled-components').DefaultTheme | Record<string, any>} theme
 */
export function getInsideChrome(theme) {
    const dark = insideIsDark(theme);
    const c = colors;

    if (dark) {
        return {
            insideRootBg: theme?.colors?.background?.default ?? DARK_BG,
            /** Strip behind the rounded module rail (contrast vs elevated surface). */
            moduleRailGutterBg: theme?.colors?.background?.default ?? DARK_BG,
            moduleRailBg: rgbaFromHex(c.neutral[900], 1),
            moduleRailBorder: rgbaFromHex(c.neutral[0], 0.08),
            moduleRailDivider: rgbaFromHex(c.neutral[0], 0.12),
            railIdleIcon: rgbaFromHex(c.neutral[0], 0.55),
            railHoverFg: c.neutral[0],
            railHoverBg: rgbaFromHex(c.neutral[0], 0.06),
            railActiveBg: moduleItemSelectedBg,
            railActiveFg: c.primary[500],
            featureShellBg: rgbaFromHex(c.neutral[900], 1),
            featureShellBorder: rgbaFromHex(c.neutral[0], 0.08),
            featureHeading: c.primary[500],
            featureNavFg: rgbaFromHex(c.neutral[0], 0.82),
            featureNavIcon: '#A3A3A3',
            featureNavHoverFg: c.neutral[0],
            featureNavHoverIcon: '#A3A3A3',
            featureNavHoverBg: rgbaFromHex(c.neutral[0], 0.06),
            featureNavActiveBg: moduleItemSelectedBg,
            featureNavActiveFg: `var(--ant-color-primary, ${c.primary[500]})`,
            onlineDot: c.success[500],
            onlineDotRing: rgbaFromHex(c.neutral[900], 1),
            appHeaderBg: rgbaFromHex(c.neutral[900], 1),
            breadcrumbMuted: rgbaFromHex(c.neutral[0], 0.45),
            crumbCurrent: `var(--ant-color-primary, ${c.primary[500]})`,
            searchBg: rgbaFromHex(c.neutral[0], 0.06),
            searchBorder: rgbaFromHex(c.neutral[0], 0.1),
            searchHotkey: rgbaFromHex(c.neutral[0], 0.45),
            ghostBtnBorder: rgbaFromHex(c.neutral[0], 0.12),
            ghostBtnFg: rgbaFromHex(c.neutral[0], 0.85),
            ghostBtnHoverBg: rgbaFromHex(c.neutral[0], 0.06),
            ghostBtnHoverBorder: rgbaFromHex(c.neutral[0], 0.18),
            headerIconDefault: rgbaFromHex(c.neutral[0], 0.65),
            headerIconActiveBg: layoutAccentWash,
            headerIconActiveFg: c.primary[500],
            headerIconHoverBg: rgbaFromHex(c.neutral[0], 0.08),
            headerIconHoverFg: c.neutral[0],
            pageScrollLockedBg: theme?.colors?.background?.secondary ?? rgbaFromHex(c.neutral[900], 1),
            scrollbarThumb: rgbaFromHex(c.neutral[0], 0.22),
            scrollbarThumbHover: rgbaFromHex(c.neutral[0], 0.38),
            panelDivider: rgbaFromHex(c.neutral[0], 0.08),
            mutedControlBg: rgbaFromHex(c.neutral[0], 0.06),
            mutedControlBorder: rgbaFromHex(c.neutral[0], 0.12),
        };
    }

    return {
        insideRootBg: c.neutral[100],
        /** Visible channel so the 64px rounded rail reads as a card (not same as `#fff` surface). */
        moduleRailGutterBg: c.neutral[200],
        moduleRailBg: c.neutral[0],
        moduleRailBorder: '#EEEEEE',
        moduleRailDivider: c.neutral[200],
        railIdleIcon: '#A3A3A3',
        railHoverFg: c.neutral[900],
        railHoverBg: c.neutral[100],
        railActiveBg: moduleItemSelectedBg,
        railActiveFg: c.primary[600],
        featureShellBg: c.neutral[0],
        featureShellBorder: '#EEEEEE',
        featureHeading: c.primary[700],
        featureNavFg: c.neutral[900],
        featureNavIcon: '#A3A3A3',
        featureNavHoverFg: c.neutral[900],
        featureNavHoverIcon: '#A3A3A3',
        featureNavHoverBg: c.neutral[100],
        featureNavActiveBg: moduleItemSelectedBg,
        featureNavActiveFg: `var(--ant-color-primary, ${c.primary[600]})`,
        onlineDot: c.success[500],
        onlineDotRing: c.neutral[0],
        appHeaderBg: c.neutral[0],
        breadcrumbMuted: '#737373',
        crumbCurrent: `var(--ant-color-primary, ${c.primary[600]})`,
        searchBg: c.neutral[50],
        searchBorder: c.neutral[200],
        searchHotkey: c.neutral[500],
        ghostBtnBorder: c.neutral[200],
        ghostBtnFg: c.neutral[900],
        ghostBtnHoverBg: c.neutral[50],
        ghostBtnHoverBorder: c.neutral[200],
        headerIconDefault: c.neutral[500],
        headerIconActiveBg: layoutAccentWash,
        headerIconActiveFg: c.primary[600],
        headerIconHoverBg: c.neutral[100],
        headerIconHoverFg: c.neutral[900],
        pageScrollLockedBg: c.neutral[0],
        scrollbarThumb: c.neutral[200],
        scrollbarThumbHover: c.neutral[500],
        panelDivider: c.neutral[200],
        mutedControlBg: rgbaFromHex(c.neutral[900], 0.03),
        mutedControlBorder: rgbaFromHex(c.neutral[900], 0.08),
    };
}

export const darkBackgroundDefault = DARK_BG;

/** Light-mode main-column header: horizontal wash + bleed height (see `HeaderChrome`). */
export const headerLight = {
    gradientLeft: '#edf1fd',
    gradientRight: '#f2eefc',
    /** Light main column: horizontal wash only for this band from the top; below is solid sidebar-white. */
    gradientHeightPx: 120,
    /**
     * Vertical mask on the gradient band: full opacity from top to this %, then feather to transparent
     * (reads as a soft / “blurry” edge into white, not a hard line).
     */
    gradientFadeOpaqueStopPct: 42,
};
